const MANAGED_SUFFIX_SELECTOR = [
    '[data-composer-signature="true"]',
    '[data-composer-separator="true"]',
    '[data-composer-original-communication="true"]',
    '[data-composer-unsubscribe="true"]',
    '.unsubscribe-link',
].join(',');

interface GetComposerBodyContentOptions {
    normalizedSuffixHtml?: string | string[];
    suffixSourceHtml?: string | string[];
}

interface ReplaceComposerBodyContentOptions {
    normalizedSuffixHtml?: string | string[];
    suffixSourceHtml?: string | string[];
}

const createContainer = (html: string) => {
    const container = document.createElement('div');
    container.innerHTML = html || '';

    return container;
};

const normalizeHtmlText = (html: string): string =>
    (createContainer(html).textContent || '').replaceAll(/\s+/g, ' ').trim();

export const getVisibleHtmlTextLength = (html: string): number => normalizeHtmlText(html).length;

const buildHtmlFromNodes = (nodes: ChildNode[]): string => {
    const container = document.createElement('div');

    for (const node of nodes) {
        container.appendChild(node.cloneNode(true));
    }

    return container.innerHTML;
};

const getNodeHtml = (node: ChildNode): string => buildHtmlFromNodes([node]);

const normalizeHtmlList = (html: string | string[] | undefined): string[] => {
    if (Array.isArray(html)) {
        return html.filter(Boolean);
    }

    return html ? [html] : [];
};

const SIGNED_IMAGE_QUERY_PARAMETERS = new Set(['expires', 'key-pair-id', 'policy', 'signature']);

const isSignedImageQueryParameter = (parameter: string): boolean => {
    const normalizedParameter = parameter.toLowerCase();

    return (
        SIGNED_IMAGE_QUERY_PARAMETERS.has(normalizedParameter) ||
        normalizedParameter.startsWith('x-amz-')
    );
};

const normalizeImageSource = (source: string): string => {
    const trimmedSource = source.trim();

    if (!trimmedSource || trimmedSource.startsWith('data:') || trimmedSource.startsWith('cid:')) {
        return trimmedSource;
    }

    try {
        const url = new URL(trimmedSource, globalThis.location.origin);
        const hasProtocol = /^[a-z][a-z\d+.-]*:/i.test(trimmedSource);
        const isProtocolRelative = trimmedSource.startsWith('//');

        for (const parameter of Array.from(url.searchParams.keys())) {
            if (isSignedImageQueryParameter(parameter)) {
                url.searchParams.delete(parameter);
            }
        }

        const normalizedSource = `${url.pathname}${url.search}${url.hash}`;

        return hasProtocol || isProtocolRelative
            ? `${url.origin}${normalizedSource}`
            : normalizedSource;
    } catch {
        return trimmedSource;
    }
};

const isSuffixSeparatorNode = (node: ChildNode): boolean => {
    const nodeHtml = getNodeHtml(node);

    return !normalizeHtmlText(nodeHtml) || nodeHtml.toLowerCase().includes('<hr');
};

const removeNormalizedSuffix = (html: string, suffixText: string): string => {
    const container = createContainer(html);
    const nodes = Array.from(container.childNodes);

    if (!normalizeHtmlText(container.innerHTML).endsWith(suffixText)) {
        return html;
    }

    for (const [index] of nodes.entries()) {
        const candidateText = normalizeHtmlText(buildHtmlFromNodes(nodes.slice(index)));

        if (candidateText !== suffixText) {
            continue;
        }

        let suffixStartIndex = index;

        while (suffixStartIndex > 0 && isSuffixSeparatorNode(nodes[suffixStartIndex - 1])) {
            suffixStartIndex -= 1;
        }

        return buildHtmlFromNodes(nodes.slice(0, suffixStartIndex));
    }

    return html;
};

const removeNormalizedSuffixes = (
    html: string,
    normalizedSuffixHtml: GetComposerBodyContentOptions['normalizedSuffixHtml']
): string => {
    return normalizeHtmlList(normalizedSuffixHtml)
        .map((suffixHtml) => normalizeHtmlText(suffixHtml || ''))
        .filter(Boolean)
        .reduce((bodyHtml, suffixText) => removeNormalizedSuffix(bodyHtml, suffixText), html);
};

const getManagedSuffixNodes = (html: string): Element[] => {
    const container = createContainer(html);
    container.querySelector('.body')?.remove();

    return Array.from(container.querySelectorAll(MANAGED_SUFFIX_SELECTOR));
};

const getImageSources = (html: string): string[] =>
    Array.from(createContainer(html).querySelectorAll<HTMLImageElement>('img[src]'))
        .map((image) => normalizeImageSource(image.getAttribute('src') || ''))
        .filter(Boolean);

const imageSourcesMatch = (leftSources: string[], rightSources: string[]): boolean => {
    return (
        leftSources.length === rightSources.length &&
        leftSources.every((source, index) => source === rightSources[index])
    );
};

const getImageOnlyManagedSuffixSources = (
    suffixSourceHtml: string | string[] | undefined
): string[][] => {
    return normalizeHtmlList(suffixSourceHtml)
        .flatMap((sourceHtml) => getManagedSuffixNodes(sourceHtml))
        .filter((managedNode) => !normalizeHtmlText(managedNode.outerHTML))
        .map((managedNode) => getImageSources(managedNode.outerHTML))
        .filter((sources) => sources.length);
};

const removeImageOnlyManagedSuffix = (
    html: string,
    suffixSourceHtml: string | string[] | undefined
): string => {
    const imageOnlySuffixSources = getImageOnlyManagedSuffixSources(suffixSourceHtml);

    if (!imageOnlySuffixSources.length) {
        return html;
    }

    const container = createContainer(html);
    const nodes = Array.from(container.childNodes);

    for (const [index] of nodes.entries()) {
        const candidateHtml = buildHtmlFromNodes(nodes.slice(index));

        if (normalizeHtmlText(candidateHtml)) {
            continue;
        }

        const candidateImageSources = getImageSources(candidateHtml);
        const matchesSuffixImages = imageOnlySuffixSources.some((suffixSources) =>
            imageSourcesMatch(candidateImageSources, suffixSources)
        );

        if (!matchesSuffixImages) {
            continue;
        }

        let suffixStartIndex = index;

        while (suffixStartIndex > 0 && isSuffixSeparatorNode(nodes[suffixStartIndex - 1])) {
            suffixStartIndex -= 1;
        }

        return buildHtmlFromNodes(nodes.slice(0, suffixStartIndex));
    }

    return html;
};

const appendManagedSuffixNodes = (container: HTMLElement, nodes: Element[]): void => {
    for (const managedNode of nodes) {
        container.appendChild(managedNode.cloneNode(true));
    }
};

export const getComposerBodyContent = (
    html: string,
    options: GetComposerBodyContentOptions = {}
): string => {
    const container = createContainer(html);
    const bodyNode = container.querySelector('.body');

    if (bodyNode) {
        return bodyNode.innerHTML;
    }

    for (const managedNode of container.querySelectorAll(MANAGED_SUFFIX_SELECTOR)) {
        managedNode.remove();
    }

    return removeImageOnlyManagedSuffix(
        removeNormalizedSuffixes(container.innerHTML, options.normalizedSuffixHtml),
        options.suffixSourceHtml
    );
};

const hasManagedSuffixImage = (
    html: string,
    suffixSourceHtml: string | string[] | undefined
): boolean => {
    const htmlImageSources = new Set(getImageSources(html));

    if (!htmlImageSources.size) {
        return false;
    }

    return normalizeHtmlList(suffixSourceHtml).some((sourceHtml) =>
        getManagedSuffixNodes(sourceHtml).some((managedNode) =>
            getImageSources(managedNode.outerHTML).some((source) => htmlImageSources.has(source))
        )
    );
};

const shouldUseSuffixSource = (
    html: string,
    options: ReplaceComposerBodyContentOptions
): boolean => {
    const normalizedSuffixHtml = options.normalizedSuffixHtml ?? options.suffixSourceHtml;

    return Boolean(
        (normalizedSuffixHtml && removeNormalizedSuffixes(html, normalizedSuffixHtml) !== html) ||
            hasManagedSuffixImage(html, options.suffixSourceHtml)
    );
};

export const replaceComposerBodyContent = (
    html: string,
    replacementHtml: string,
    options: ReplaceComposerBodyContentOptions = {}
): string => {
    const container = createContainer(html);
    const bodyNode = container.querySelector('.body');

    if (bodyNode) {
        bodyNode.innerHTML = replacementHtml;

        return container.innerHTML;
    }

    const generatedBody = document.createElement('div');
    generatedBody.className = 'body';
    generatedBody.innerHTML = replacementHtml;
    const nextContainer = document.createElement('div');

    nextContainer.appendChild(generatedBody);

    const existingSuffixNodes = Array.from(container.querySelectorAll(MANAGED_SUFFIX_SELECTOR));

    if (existingSuffixNodes.length) {
        appendManagedSuffixNodes(nextContainer, existingSuffixNodes);

        return nextContainer.innerHTML;
    }

    if (shouldUseSuffixSource(html, options)) {
        for (const suffixSourceHtml of normalizeHtmlList(options.suffixSourceHtml)) {
            appendManagedSuffixNodes(nextContainer, getManagedSuffixNodes(suffixSourceHtml));
        }
    }

    return nextContainer.innerHTML;
};
